import React, { useState, useEffect, useContext } from 'react'
import api from '../../../../../lib/api'
import { SearchResultType } from '../../../../../lib/types'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import Typography from '@mui/material/Typography'
import { SimpleSnackbarContext } from '../../../../../components/SimpleSnackbar'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import IconButton from '@mui/material/IconButton'
import SearchCollections from '../../../../../components/SearchCollections'
import MenuItem from '@mui/material/MenuItem'

interface iCollectionMove {
  disabled: boolean
  stories: string[]
  currentCollectionId: string
  reload: () => void
  isMenu?: boolean
}

const CollectionMove: React.FC<iCollectionMove> = ({ disabled, stories, reload, currentCollectionId, isMenu }) => {
  const { actions: snackActions } = useContext(SimpleSnackbarContext)
  const [open, setOpen] = useState<boolean>(false)
  const [selectedCollection, setSelectedCollection] = useState<SearchResultType | null>(null)
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))

  useEffect(() => {
    if (open) {
      setSelectedCollection(null)
    }
  }, [open])

  const handleSelected = (value: SearchResultType) => {
    setSelectedCollection(value)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleMove = async () => {
    try {
      if (selectedCollection?.id) {
        await Promise.all(
          stories.map(
            async storyId => await api.changeStoryToCollection(storyId, selectedCollection.id, currentCollectionId)
          )
        )
        reload()
        snackActions.open(`Selected stories successfully moved to ${selectedCollection.title}`)
        handleClose()
      }
    } catch (error) {
      snackActions.open('Something was wrong! please try again.')
      throw error
    }
  }

  return (
    <>
      {isMenu ? (
        <MenuItem
          onClick={() => {
            setOpen(true)
          }}
        >
          Move to other collection
        </MenuItem>
      ) : (
        <Button disabled={disabled} color="secondary" onClick={() => setOpen(true)} startIcon={<SwapHorizIcon />}>
          Move to other collection
        </Button>
      )}
      <Dialog fullScreen={fullScreen} open={open} maxWidth="md" fullWidth onClose={handleClose}>
        <DialogTitle>
          Move to collection{' '}
          <IconButton
            aria-label="close"
            onClick={() => setOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2" fontWeight="700" marginBottom={3} gutterBottom>
              Move selected stories to a different collection
            </Typography>
            <SearchCollections
              filterId={currentCollectionId}
              handleSelect={collection => {
                if (!collection) {
                  return
                }
                handleSelected({ kind: 'COLLECTION', id: collection.id, title: collection.title })
              }}
              name="collection"
              allowAdd
              helperText=" "
            />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose} autoFocus>
            Cancel
          </Button>
          <Button color="secondary" onClick={handleMove}>
            Move
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default CollectionMove
